import { computed, type ComputedRef } from 'vue'

import { email } from '@/lib/validation'

import { isValidOnboardingPhone } from '../lib/onboardingPhoneValidation'
import type { OnboardingFormData } from '../lib/onboardingPrefill'

type Translate = (key: string, ...args: unknown[]) => string

type FieldCheck = { isFilled: boolean; labelKey: string }
type Area = { areaLabelKey: string; fields: FieldCheck[] }

const filled = (value: unknown) => Boolean((value ?? '').toString().trim())
const validEmail = (value: unknown) =>
  email((value ?? '').toString().trim()) === true && filled(value)

/**
 * The new onboarding requires several groups of fields to be filled in before
 * it can be finished. Returns what's missing grouped by the on-screen section
 * it belongs to, so callers can show one specific hint instead of several
 * generic ones across multiple failed attempts.
 */
export function useOnboardingDataCompleteness(options: {
  formData: OnboardingFormData
  platform: ComputedRef<string | null>
  isLogoEditable: ComputedRef<boolean>
  isCommissionEditable: ComputedRef<boolean>
  isUsOnboarding: ComputedRef<boolean>
  t: Translate
}) {
  const { formData, platform, isLogoEditable, isCommissionEditable, isUsOnboarding, t } = options

  const areas = computed<Area[]>(() => [
    {
      areaLabelKey: 'onboarding.wizard.data-entry.company-name-website',
      fields: [
        { isFilled: filled(formData.name), labelKey: 'onboarding.wizard.basic-info.company-name' },
        {
          isFilled: filled(formData.website),
          labelKey: 'onboarding.wizard.basic-info.website-url',
        },
      ],
    },
    {
      areaLabelKey: 'onboarding.wizard.data-entry.company-info',
      fields: [
        { isFilled: filled(formData.street), labelKey: 'onboarding.wizard.data-entry.street' },
        { isFilled: filled(formData.zipcode), labelKey: 'onboarding.wizard.data-entry.zipcode' },
        { isFilled: filled(formData.city), labelKey: 'onboarding.wizard.data-entry.city' },
        { isFilled: filled(formData.country), labelKey: 'onboarding.wizard.data-entry.country' },
        ...(isUsOnboarding.value
          ? [
              { isFilled: filled(formData.state), labelKey: 'onboarding.wizard.data-entry.state' },
              {
                isFilled: filled(formData.brokerageName),
                labelKey: 'onboarding.wizard.data-entry.brokerage-name',
              },
              {
                isFilled: filled(formData.licenseNumber),
                labelKey: 'onboarding.wizard.data-entry.license-number',
              },
            ]
          : []),
      ],
    },
    {
      areaLabelKey: 'onboarding.wizard.data-entry.responsible-person',
      fields: [
        {
          isFilled: filled(formData.contactFirstName),
          labelKey: 'onboarding.wizard.data-entry.contact-first-name',
        },
        {
          isFilled: filled(formData.contactLastName),
          labelKey: 'onboarding.wizard.data-entry.contact-last-name',
        },
        { isFilled: validEmail(formData.contactEmail), labelKey: 'onboarding.submit.fields.email' },
        {
          isFilled: isValidOnboardingPhone(formData.contactPhone),
          labelKey: 'onboarding.wizard.data-entry.phone',
        },
      ],
    },
    {
      areaLabelKey: 'onboarding.wizard.data-entry.public-facing-ambassador',
      fields: [
        {
          isFilled: filled(formData.ambassadorFirstName),
          labelKey: 'onboarding.wizard.data-entry.contact-first-name',
        },
        {
          isFilled: filled(formData.ambassadorLastName),
          labelKey: 'onboarding.wizard.data-entry.contact-last-name',
        },
        { isFilled: filled(formData.ambassadorRole), labelKey: 'onboarding.submit.fields.role' },
        {
          isFilled: validEmail(formData.ambassadorEmail),
          labelKey: 'onboarding.submit.fields.email',
        },
        {
          isFilled: isValidOnboardingPhone(formData.ambassadorMobile),
          labelKey: 'onboarding.wizard.data-entry.mobile',
        },
        ...(platform.value !== 'turkoglu'
          ? [
              {
                isFilled: isValidOnboardingPhone(formData.phone),
                labelKey: 'onboarding.wizard.data-entry.phone',
              },
            ]
          : []),
        ...(isCommissionEditable.value
          ? [
              {
                isFilled: filled(formData.commission),
                labelKey: 'onboarding.wizard.data-entry.commission-rate',
              },
            ]
          : []),
      ],
    },
    {
      areaLabelKey: 'onboarding.wizard.data-entry.brand-colors',
      fields: [
        {
          isFilled: filled(formData.primaryColor),
          labelKey: 'onboarding.wizard.data-entry.primary-color',
        },
        {
          isFilled: filled(formData.secondaryColor),
          labelKey: 'onboarding.wizard.data-entry.secondary-color',
        },
      ],
    },
    {
      areaLabelKey: 'onboarding.wizard.data-entry.company-images',
      fields: [
        {
          isFilled: filled(formData.portraitImage),
          labelKey: 'settings.company.images.labels.portrait-picture',
        },
        ...(isLogoEditable.value
          ? [
              {
                isFilled: filled(formData.companyLogo),
                labelKey: 'settings.company.images.labels.company-logo',
              },
            ]
          : []),
      ],
    },
    {
      areaLabelKey: 'onboarding.wizard.review.legal-urls-title',
      fields: [
        { isFilled: filled(formData.linkImprint), labelKey: 'onboarding.company-data.web.imprint' },
        { isFilled: filled(formData.linkPrivacy), labelKey: 'onboarding.company-data.web.privacy' },
        { isFilled: filled(formData.linkAbout), labelKey: 'onboarding.company-data.web.about' },
      ],
    },
  ])

  const missingFieldsByArea = computed(() =>
    areas.value
      .map((area) => ({
        areaLabel: t(area.areaLabelKey),
        missingFieldLabels: area.fields
          .filter((field) => !field.isFilled)
          .map((field) => t(field.labelKey)),
      }))
      .filter((area) => area.missingFieldLabels.length > 0)
  )

  const hasMissingOnboardingData = computed(() => missingFieldsByArea.value.length > 0)

  const missingOnboardingDataSummary = computed(() =>
    missingFieldsByArea.value
      .map((area) => `${area.areaLabel}: ${area.missingFieldLabels.join(', ')}`)
      .join('\n')
  )

  return { hasMissingOnboardingData, missingOnboardingDataSummary }
}
